import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useCoAgent, useCopilotChat } from '@copilotkit/react-core'

import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'

import { AiAgentGlobalActions } from 'modules/ai/components/AiAgentGlobalActions'

export type OneSharedState<T = unknown> = {
  platform: 'web' | 'mobile'
  requiredSkills: string[]
  coCreationData: T | null
  currentPage: {
    url: string
    title: string
  }
}

type OneContextValue = {
  state: OneSharedState
  setState: (data: OneSharedState) => void
  isThinking: boolean
}

const OneContext = React.createContext<OneContextValue | null>(null)

const DisabledOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const initialState = React.useMemo<OneSharedState>(() => {
    return {
      platform: 'web',
      requiredSkills: [],
      coCreationData: null,
      currentPage: {
        url: window.location.href,
        title: document.title,
      },
    }
  }, [])

  const [state, setState] = React.useState<OneSharedState>(initialState)

  React.useEffect(() => {
    const nextPage = { url: window.location.href, title: document.title }
    if (state.currentPage.url === nextPage.url) {
      return
    }
    setState({ ...state, currentPage: nextPage })
  }, [location.key, setState, state])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking: false }
  }, [state, setState])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

const InternalOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const initialState = React.useMemo<OneSharedState>(() => {
    return {
      platform: 'web',
      requiredSkills: [],
      coCreationData: null,
      currentPage: {
        url: window.location.href,
        title: document.title,
      },
    }
  }, [])

  const { state, setState } = useCoAgent<OneSharedState>({
    name: 'one-workflow',
    initialState,
  })

  const { isLoading: isThinking } = useCopilotChat()

  // Always keep current page in sync. Do not apply any change when the URL is an activity (hash).
  React.useEffect(() => {
    const nextPage = { url: window.location.href, title: document.title }
    if (window.location.hash) return
    if (state.currentPage.url === nextPage.url && state.currentPage.title === nextPage.title) {
      return
    }
    if (state.currentPage.url !== nextPage.url) {
      setState({ ...state, currentPage: nextPage, coCreationData: null, requiredSkills: [] })
    } else {
      setState({ ...state, currentPage: nextPage })
    }
  }, [location.key, setState, state])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking }
  }, [state, setState, isThinking])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

type UseOneResult<T> = {
  coCreationData: T
  setCoCreationData: (data: T) => void
  isThinking: boolean
}

export const useOne = <T,>(requiredSkills: string[], coCreationData: T): UseOneResult<T> => {
  const ctx = React.useContext(OneContext)
  if (!ctx) {
    throw new Error('useOne must be used within OneProvider')
  }

  const { data: dataFromRedirect } = useOneCreateActionState<T>()

  React.useEffect(() => {
    if (ctx.state.coCreationData !== null) return

    const newCoCreationData = dataFromRedirect ?? coCreationData
    const initialState: OneSharedState<T> = {
      platform: 'web' as const,
      requiredSkills,
      coCreationData: newCoCreationData,
      currentPage: { url: window.location.href, title: document.title },
    }

    ctx.setState(initialState)
  }, [ctx, coCreationData, requiredSkills, dataFromRedirect])

  const setCoCreationData = React.useCallback(
    (data: T) => {
      ctx.setState({ ...ctx.state, coCreationData: data })
    },
    [ctx]
  )

  return {
    coCreationData: ctx.state.coCreationData as T,
    setCoCreationData: setCoCreationData,
    isThinking: ctx.isThinking,
  }
}

export const OneProvider = ({
  children,
  enabled,
}: {
  children: React.ReactNode
  enabled: boolean
}) => {
  if (enabled) {
    return (
      <InternalOneProvider>
        <AiAgentGlobalActions />
        {children}
      </InternalOneProvider>
    )
  }
  return <DisabledOneProvider>{children}</DisabledOneProvider>
}
